import { forwardRef } from 'react';
import { Car } from 'lucide-react';

const VehiclePDF = forwardRef(({ vehicle, calculations }, ref) => {
    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(price);
    };

    const formatDate = () => {
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date());
    };

    const getMarginStatus = (margin) => {
        if (margin >= 1000) return { label: 'Excellente', color: '#10B981' };
        if (margin >= 500) return { label: 'Correcte', color: '#F59E0B' };
        return { label: 'Faible', color: '#EF4444' };
    };

    const marginStatus = getMarginStatus(calculations.margeNetteHT);

    return (
        <div ref={ref} className="pdf-container bg-white text-black p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
            {/* Header */}
            <div className="flex items-center justify-between border-b-2 border-gray-800 pb-4 mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-[#6366F1] flex items-center justify-center">
                        <Car className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">MARGE AUTO</h1>
                        <p className="text-sm text-gray-600">Fiche de calcul véhicule</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-sm text-gray-600">Date d'édition</p>
                    <p className="font-medium">{formatDate()}</p>
                </div>
            </div>

            {/* Vehicle Info */}
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 uppercase tracking-wide border-b border-gray-300 pb-2">
                    Informations véhicule
                </h2>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs text-gray-500 uppercase">Désignation</p>
                        <p className="font-semibold text-gray-900">{vehicle.nom || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase">Modèle</p>
                        <p className="font-semibold text-gray-900">{vehicle.modele || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase">Immatriculation</p>
                        <p className="font-semibold text-gray-900 tracking-wider">{vehicle.immatriculation || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase">Référence dossier</p>
                        <p className="font-semibold text-gray-900">{vehicle.id?.slice(-8).toUpperCase() || '-'}</p>
                    </div>
                </div>
            </div>

            {/* Transaction */}
            <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 uppercase tracking-wide border-b border-gray-300 pb-2">
                    Transaction
                </h2>
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Prix d'achat</span>
                        <span className="font-semibold">{formatPrice(vehicle.prixAchat)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Prix de vente</span>
                        <span className="font-semibold">{formatPrice(vehicle.prixVente)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Frais de mise en circulation</span>
                        <span className="font-semibold">{formatPrice(vehicle.fraisCirculation)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Remise client</span>
                        <span className="font-semibold text-red-600">- {formatPrice(vehicle.remiseClient)}</span>
                    </div>
                </div>
            </div>

            {/* Calcul initial */}
            <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 uppercase tracking-wide border-b border-gray-300 pb-2">
                    Détail du calcul
                </h2>
                <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Vente réelle TTC</span>
                        <span className="font-semibold">{formatPrice(calculations.venteReelleTTC)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Marge brute TTC</span>
                        <span className="font-semibold">{formatPrice(calculations.margeBruteTTC)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">TVA sur marge (20%)</span>
                        <span className="font-semibold text-red-600">- {formatPrice(calculations.tvaSurMarge)}</span>
                    </div>
                    <div className="flex justify-between py-2 bg-gray-100 px-2 rounded">
                        <span className="font-medium text-gray-800">Marge brute HT avant frais</span>
                        <span className="font-bold text-gray-900">{formatPrice(calculations.margeBruteHTAvantFrais)}</span>
                    </div>
                </div>
            </div>

            {/* Frais détaillés */}
            <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 uppercase tracking-wide border-b border-gray-300 pb-2">
                    Détail des frais liés au véhicule
                </h2>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-200">
                            <th className="text-left py-2 px-2 font-semibold">Frais</th>
                            <th className="text-right py-2 px-2 font-semibold">TTC</th>
                            <th className="text-center py-2 px-2 font-semibold">TVA Récup.</th>
                            <th className="text-right py-2 px-2 font-semibold">HT</th>
                            <th className="text-right py-2 px-2 font-semibold">TVA Déduc.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {calculations.fraisDetails.filter(f => f.montant_ttc > 0).map((fee) => (
                            <tr key={fee.id || fee.nom} className="border-b border-gray-200">
                                <td className="py-2 px-2">{fee.nom}</td>
                                <td className="text-right py-2 px-2">{formatPrice(fee.montant_ttc)}</td>
                                <td className="text-center py-2 px-2">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${fee.tva_recuperable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                        {fee.tva_recuperable ? 'Oui' : 'Non'}
                                    </span>
                                </td>
                                <td className="text-right py-2 px-2">{formatPrice(fee.ht)}</td>
                                <td className="text-right py-2 px-2 text-green-600">
                                    {fee.tva > 0 ? formatPrice(fee.tva) : '-'}
                                </td>
                            </tr>
                        ))}
                        {calculations.fraisDetails.filter(f => f.montant_ttc > 0).length === 0 && (
                            <tr>
                                <td colSpan={5} className="py-4 text-center text-gray-500 italic">
                                    Aucun frais enregistré
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Totaux */}
            <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 uppercase tracking-wide border-b border-gray-300 pb-2">
                    Totaux
                </h2>
                <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Total frais TTC</span>
                        <span className="font-semibold">{formatPrice(calculations.totalFraisTTC)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Total frais HT</span>
                        <span className="font-semibold text-red-600">- {formatPrice(calculations.totalFraisHT)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Total TVA déductible sur frais</span>
                        <span className="font-semibold text-green-600">+ {formatPrice(calculations.totalTVADeductible)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">TVA nette dossier</span>
                        <span className={`font-semibold ${calculations.tvaNetteDossier >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {calculations.tvaNetteDossier >= 0 ? '- ' : '+ '}{formatPrice(Math.abs(calculations.tvaNetteDossier))}
                        </span>
                    </div>
                </div>
            </div>

            {/* Marge finale */}
            <div className="bg-gray-900 text-white rounded-lg p-6 text-center">
                <p className="text-sm uppercase tracking-wider mb-2 opacity-80">Marge nette HT réelle</p>
                <p className="text-4xl font-bold" style={{ color: marginStatus.color }}>
                    {formatPrice(calculations.margeNetteHT)}
                </p>
                <p className="text-sm mt-2 uppercase tracking-wide" style={{ color: marginStatus.color }}>
                    {marginStatus.label}
                </p>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t border-gray-300 text-center text-xs text-gray-500">
                <p>Document généré par Marge Auto - Calculateur de marge pour véhicules d'occasion</p>
                <p className="mt-1">Ce document est confidentiel et destiné à un usage interne uniquement.</p>
            </div>
        </div>
    );
});

VehiclePDF.displayName = 'VehiclePDF';

export default VehiclePDF;
